import classNames from 'classnames';
import { observer } from 'mobx-react';

import NavButton from '../shared/components/Button/NavButton';
import HeaderLevel1 from '../shared/components/Typography/HeaderLevel1';
import { HubSpotFormPage } from '../shared/hubSpot/HubSpotFormPage';
import ProductSummary from '../shared/hubSpot/ProductSummary/ProductSummary';
import ProductPromotionInfo from './ProductPromotionInfo/ProductPromotionInfo';
import { RequestQuoteConfirmation } from './RequestQuoteConfirmation';
import css from './RequestQuotePage.css';
import { RequestQuoteFormData, RequestQuoteState } from './RequestQuoteState';
import RequestQuoteSteps from './RequestQuoteSteps/RequestQuoteSteps';

const REQUEST_QUOTE_FORM_FIELD_NAMES = {
  consentGranted: 'LEGAL_CONSENT.subscription_type_1257116',
  countryName: 'country',
  quantity: 'flokk__price_request_quantity',
  representingCompany: 'flokk__representing_a_company',
} as const;

export function getRequestQuoteFormData(data: Record<string, string | string[]>): RequestQuoteFormData {
  const getValue = (name: string): string | undefined => {
    const value = data[name];
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const representingCompanyValue = getValue(REQUEST_QUOTE_FORM_FIELD_NAMES.representingCompany);
  const quantityValue = getValue(REQUEST_QUOTE_FORM_FIELD_NAMES.quantity);

  return {
    countryName: getValue(REQUEST_QUOTE_FORM_FIELD_NAMES.countryName),
    customerType:
      representingCompanyValue === 'Yes' ? 'Business' : representingCompanyValue === 'No' ? 'Private' : undefined,
    consentGranted: getValue(REQUEST_QUOTE_FORM_FIELD_NAMES.consentGranted) === 'true',
    quantity: quantityValue ? Number.parseInt(quantityValue, 10) : 1,
  };
}

@observer
class RequestQuotePage extends HubSpotFormPage<RequestQuoteState> {
  get initialFormData() {
    const data: Record<string, string> = {};

    if (this.props.state.hasConfigurationCode()) {
      const code = this.props.state.getCode();
      const url = this.props.state.getShopUrl();
      const facilityInfo = this.props.state.productFacilityInfoResponse?.facilityInfo;

      const promotionInfo = this.props.state.promotionResponse?.isPromoted
        ? `(${this.props.state.promotionResponse.promotionName})`
        : '';

      data.latest_configuration_id = `${code} ${promotionInfo}`;
      data.latest_configuration_url = url;
      data.latest_configuration_production_site_id = facilityInfo?.facilityNumber ?? '';
      data.latest_configuration_production_site_name = facilityInfo?.facilityName ?? '';
    } else {
      data.latest_configuration_id = 'na';
      data.latest_configuration_url = 'na';
      data.latest_configuration_production_site_id = 'na';
      data.latest_configuration_production_site_name = 'na';
    }

    data.latest_configuration_product_id = this.props.state.product.id;
    data.latest_configuration_brand = this.props.state.product.brandId;
    data.price_quote_request = true.toString();

    return data;
  }

  onFormSubmissionSuccess(data: Record<string, string | string[]>) {
    this.props.state.setFormSubmittedData(getRequestQuoteFormData(data));
  }

  getTranslation() {
    const {
      state,
      appState: {
        translation: { requestQuotePage },
      },
    } = this.props;

    if (state.isRequestProcessedByDealer) {
      return requestQuotePage.contentWhenProcessedByDealer;
    }

    return requestQuotePage.defaultContent;
  }

  renderMainContent() {
    const {
      appState: { translation },
      state,
    } = this.props;

    return (
      <div className={css.container}>
        <div className={css.formContainer} id="hsRequestQuoteForm">
          {this.props.appState.store.canRequestQuote() ? super.render() : null}
        </div>
        <ProductSummary
          shoppingCartClassName={css.shoppingCartContainer}
          productPreviewClassName={css.productDetailsPreviewContainer}
          state={state}
          translation={translation.cart.summary}
        />
      </div>
    );
  }

  renderSteps() {
    const translation = this.getTranslation();
    return <RequestQuoteSteps className={css.steps} translation={translation} />;
  }

  render() {
    const {
      appState: { navigationState },
      state,
    } = this.props;
    const translation = this.getTranslation();

    if (state.isFormSubmitted) {
      return (
        <RequestQuoteConfirmation
          isEmbedded={state.isEmbedded}
          navigation={navigationState}
          translation={translation.confirmation}
        />
      );
    }

    return (
      <div className={css.page}>
        {!state.isEmbedded && (
          <NavButton className={css.navigation} onClick={state.backToSummary} back>
            {translation.goBackLink}
          </NavButton>
        )}
        <HeaderLevel1 className={classNames('visuallyHidden', css.title)} centered>
          {translation.title}
        </HeaderLevel1>
        <ProductPromotionInfo promotionDescription={state.promotionDescription} />

        {state.isRequestProcessedByDealer ? (
          <>
            {this.renderSteps()}
            {this.renderMainContent()}
          </>
        ) : (
          <>
            {this.renderMainContent()}
            {this.renderSteps()}
          </>
        )}
      </div>
    );
  }
}

export default RequestQuotePage;
